import type {
  CapabilityPolicy,
  EmbeddingProvider,
  FabricPrincipal,
  FilesystemSource,
  ModelProvider,
  SecretProvider,
  SessionStore,
  ToolDef,
} from "@fabric-harness/sdk";
import { createSearchTool } from "@fabric-harness/sdk";
import { DatabricksAgentEndpointClient, databricksManagedAgentTool } from "./agent-endpoints.js";
import type { DatabricksManagedAgentToolOptions } from "./agent-endpoints.js";
import { databricksAgentServices } from "./agent-services.js";
import type { DatabricksAgentServices, DatabricksAgentServicesOptions } from "./agent-services.js";
import {
  ensureDatabricksAiGateway,
  listDatabricksModelProviderServices,
  listDatabricksModelServices,
} from "./ai-gateway.js";
import type {
  DatabricksModelService,
  DatabricksModelServiceDiscoveryOptions,
} from "./ai-gateway.js";
import { databricksAiSearchAdminTools } from "./ai-search-admin.js";
import type { DatabricksAiSearchAdminPolicy } from "./ai-search-admin.js";
import { databricksAiQueryTool } from "./ai.js";
import type { DatabricksAiQueryPolicy } from "./ai.js";
import {
  type DatabricksAssetBundleLifecycle,
  type DatabricksAssetBundleMapOptions,
  databricksAssetBundleLifecycle,
  databricksAssetBundleLifecycles,
  databricksAssetBundleTools,
  isDatabricksAssetBundleEntry,
  requireDatabricksAssetBundleMap,
} from "./asset-bundles.js";
import { databricksConsumption, databricksConsumptionTool } from "./consumption.js";
import type { DatabricksConsumption } from "./consumption.js";
import { databricksActualCostSource, databricksTenantCostLimit } from "./cost-budget-actuals.js";
import type { DatabricksActualCostSourceOptions } from "./cost-budget-actuals.js";
import { databricksEmbeddings } from "./embeddings.js";
import { databricksFeatureLookupTool } from "./feature-serving.js";
import {
  type DatabricksGenieAgentModeClient,
  databricksGenieAgentMode,
  databricksGenieAgentModeTool,
} from "./genie-agent-mode.js";
import {
  type DatabricksGenieAdmin,
  databricksGenieAdmin,
  databricksGenieAuthoringTools,
} from "./genie-authoring.js";
import { databricksGenieTool } from "./genie.js";
import { databricksGovernancePolicy, withGovernanceTools } from "./governance.js";
import { validateDatabricksGovernanceDescriptor } from "./governance.js";
import type { DatabricksLineageRecord } from "./governance.js";
import {
  databricksCredentials,
  databricksCredentialsFromTokenProvider,
  databricksIdentity,
  fabricPrincipalFor,
} from "./identity.js";
import type { DatabricksPrincipal, DatabricksTokenProvider } from "./identity.js";
import { databricksJobsAuthoringTools, validateDatabricksComputePolicy } from "./jobs-authoring.js";
import type { DatabricksComputePolicy } from "./jobs-authoring.js";
import { lakebaseClient } from "./lakebase.js";
import type { LakebaseClient, LakebasePoolFactory } from "./lakebase.js";
import { databricksLakeflowAuthoringTools } from "./lakeflow-authoring.js";
import { databricksLakeflowTools } from "./lakeflow.js";
import type { DatabricksPipelineRunPolicy } from "./lakeflow.js";
import { databricksManagedMemory } from "./managed-memory.js";
import type {
  DatabricksManagedMemoryClient,
  DatabricksManagedMemoryOptions,
} from "./managed-memory.js";
import { connectDatabricksManagedMcpServer } from "./mcp.js";
import type { DatabricksManagedMcpServerConfig } from "./mcp.js";
import { mlflowTraceExporter } from "./mlflow-tracing.js";
import type { MlflowTraceExporter } from "./mlflow-tracing.js";
import { databricksFoundationModelProvider, registerDatabricksModelProvider } from "./model.js";
import type { DatabricksModelOptions } from "./model.js";
import { createDatabricksNativeClients, stableDatabricksSdkClients } from "./native-sdk.js";
import type { DatabricksSdkClients } from "./native-sdk.js";
import { DatabricksRawProtocolClient } from "./raw-protocol-client.js";
import { databricksSecretsAuthoringTools } from "./secrets-authoring.js";
import { databricksServingAdminTools } from "./serving-admin.js";
import { listDatabricksSqlWarehouses } from "./sql-warehouses.js";
import { databricksSupervisorAgents } from "./supervisor-agents.js";
import type {
  DatabricksSupervisorAgents,
  DatabricksSupervisorAgentsOptions,
} from "./supervisor-agents.js";
import {
  databricksSqlReadTool,
  databricksSqlTool,
  databricksTableInfoTool,
  databricksWorkspaceSource,
  unityCatalogTablesTool,
} from "./tools.js";
import type { DatabricksWorkspaceSourceOptions } from "./tools.js";
import { databricksUnityCatalogAdminTools } from "./unity-catalog-admin.js";
import { databricksAiSearch } from "./vector-search.js";
import type { DatabricksAiSearchRetriever } from "./vector-search.js";
import { databricksWorkspaceAuthoringTools } from "./workspace-authoring.js";

export {
  connectDatabricksManagedMcpServer,
  databricksManagedMcpUrl,
} from "./mcp.js";
export type {
  ConnectDatabricksManagedMcpOptions,
  DatabricksManagedMcpEndpoint,
  DatabricksManagedMcpServerConfig,
} from "./mcp.js";
export {
  deployDatabricksAppArtifact,
  destroyDatabricksAppArtifact,
  runDatabricksCommand,
  validateDatabricksAppArtifact,
} from "./app-deployment.js";
export {
  DatabricksAgentEndpointClient,
  databricksManagedAgentTool,
  databricksResponsesText,
} from "./agent-endpoints.js";
export type {
  DatabricksAgentEndpointInvocation,
  DatabricksAgentEndpointResponse,
  DatabricksManagedAgentKind,
  DatabricksManagedAgentToolOptions,
  DatabricksManagedAgentToolResult,
  DatabricksResponsesContent,
  DatabricksResponsesOutputItem,
} from "./agent-endpoints.js";
export { DatabricksManagedMemoryClient, databricksManagedMemory } from "./managed-memory.js";
export type {
  DatabricksManagedMemoryOptions,
  DatabricksMemoryEntry,
  DatabricksMemoryEntryEdit,
  DatabricksMemoryEntryPage,
  DatabricksMemoryStore,
  DatabricksMemoryStorePage,
} from "./managed-memory.js";
export { DatabricksSupervisorAgents, databricksSupervisorAgents } from "./supervisor-agents.js";
export type {
  DatabricksSupervisorAgentListOptions,
  DatabricksSupervisorAgentsOptions,
} from "./supervisor-agents.js";
export type {
  DatabricksCommandResult,
  DatabricksCommandRunner,
  DestroyDatabricksAppArtifactOptions,
  DestroyDatabricksAppArtifactResult,
  DeployDatabricksAppArtifactOptions,
  DeployDatabricksAppArtifactResult,
  ValidateDatabricksAppArtifactOptions,
  ValidateDatabricksAppArtifactResult,
} from "./app-deployment.js";
export {
  DatabricksAssetBundleLifecycle,
  databricksAssetBundleLifecycle,
  databricksAssetBundleLifecycles,
  databricksAssetBundleTools,
} from "./asset-bundles.js";
export type {
  DatabricksAssetBundleMapOptions,
  DatabricksAssetBundleOptions,
} from "./asset-bundles.js";
export { digestDatabricksArtifact } from "./artifact-digest.js";
export { databricksWorkspaceOrigin } from "./workspace-origin.js";
export { databricksSdk } from "./native-sdk.js";
export type { DatabricksSdkClients, DatabricksSdkOptions } from "./native-sdk.js";
export {
  DatabricksManagedResourceConflictError,
  DatabricksUnmanagedResourceError,
  LakebaseDatabricksManagedResourceStore,
  MemoryDatabricksManagedResourceStore,
  databricksResourceFingerprint,
  requireDatabricksManagedResource,
} from "./managed-resource.js";
export type {
  DatabricksManagedResourceRecord,
  DatabricksManagedResourceStore,
  LakebaseDatabricksManagedResourceStoreOptions,
} from "./managed-resource.js";
export {
  normalizeDatabricksGenieAgentSpec,
  serializeDatabricksGenieAgentSpec,
  validateDatabricksGenieSqlReferences,
} from "./genie-authoring.js";
export {
  DatabricksGenieAgentModeProtocolError,
  DatabricksGenieAgentModeResponseError,
  DatabricksGenieAgentModeTimeoutError,
  databricksGenieAgentModeTool,
} from "./genie-agent-mode.js";
export type {
  DatabricksGenieAgentModeClient,
  DatabricksGenieAgentModeClientOptions,
  DatabricksGenieAgentModeCompletedEvent,
  DatabricksGenieAgentModeCreatedEvent,
  DatabricksGenieAgentModeErrorInfo,
  DatabricksGenieAgentModeEvent,
  DatabricksGenieAgentModeFailedEvent,
  DatabricksGenieAgentModeItemPage,
  DatabricksGenieAgentModeOutputEvent,
  DatabricksGenieAgentModeResponse,
  DatabricksGenieAgentModeToolOptions,
  DatabricksGenieAgentModeUnknownEvent,
} from "./genie-agent-mode.js";
export type {
  DatabricksGenieAdmin,
  DatabricksGenieAdminOptions,
  DatabricksGenieAuthoringToolOptions,
  DatabricksGenieAgent,
  DatabricksGenieAgentPage,
  DatabricksGenieAgentSpecV2,
  DatabricksGenieAgentUpdate,
  DatabricksGenieColumnSpec,
  DatabricksGenieDataSource,
  DatabricksGenieExport,
  DatabricksGenieOwnershipContext,
  DatabricksGeniePermissionChange,
  DatabricksGenieSqlExample,
  DatabricksGenieSqlFunction,
  DatabricksGenieJoinSide,
  DatabricksGenieJoinSpec,
  DatabricksGenieSqlSnippet,
  DatabricksGenieSqlSnippets,
  DatabricksGenieBenchmark,
  DatabricksGenieSqlPolicy,
  DatabricksGenieSqlPolicyInput,
  DatabricksGenieSqlPolicyResult,
  DatabricksGeniePermissionLevel,
  DatabricksGeniePermissions,
  DatabricksGenieAccessControl,
} from "./genie-authoring.js";
export {
  DATABRICKS_AGENT_SERVICES_API,
  DATABRICKS_AGENT_SERVICE_SECURABLE,
} from "./agent-services.js";
export type {
  DatabricksAgentServices,
  DatabricksAgentService,
  DatabricksAgentServiceConfig,
  DatabricksAgentServiceConnection,
  DatabricksAgentServiceCreateOptions,
  DatabricksAgentServiceGrantChange,
  DatabricksAgentServiceList,
  DatabricksAgentServicePermission,
  DatabricksAgentServicePermissions,
  DatabricksAgentServicePrivilege,
  DatabricksAgentServicesOptions,
  DatabricksAgentServiceUpdateOptions,
} from "./agent-services.js";
export {
  databricksSqlReadTool,
  databricksSqlTool,
  databricksRunJobTool,
  databricksNotebookTool,
  databricksMlflowLogMetricTool,
  databricksMlflowLogParamTool,
  unityCatalogTablesTool,
  databricksTableInfoTool,
} from "./tools.js";
export type {
  DatabricksMlflowRunPolicy,
  DatabricksSqlClient,
  DatabricksSqlExecutionPolicy,
  DatabricksSqlReadToolOptions,
  DatabricksSqlStatementInput,
  DatabricksSqlToolOptions,
  DatabricksWorkspaceSourceOptions,
} from "./tools.js";
export { DatabricksResourceNotAllowedError } from "./resource-policy.js";
export {
  DatabricksJobsAuthoring,
  databricksJobsAuthoring,
  databricksJobsAuthoringTools,
  validateDatabricksComputePolicy,
  validateJobSpec,
} from "./jobs-authoring.js";
export type {
  DatabricksComputePolicy,
  DatabricksCreateJobOptions,
  DatabricksCreatedJob,
  DatabricksDbtTaskSpec,
  DatabricksJobField,
  DatabricksJobSchedule,
  DatabricksJobSpec,
  DatabricksJobTask,
  DatabricksJobUpdate,
  DatabricksNewClusterSpec,
  DatabricksNotebookTaskSpec,
  DatabricksPythonWheelTaskSpec,
  DatabricksSparkJarTaskSpec,
  DatabricksSqlTaskSpec,
} from "./jobs-authoring.js";
export {
  DatabricksLakeflowAuthoring,
  databricksLakeflowAuthoringTools,
} from "./lakeflow-authoring.js";
export type { DatabricksPipelineLibrary, DatabricksPipelineSpec } from "./lakeflow-authoring.js";
export { DatabricksAiSearchAdmin, databricksAiSearchAdminTools } from "./ai-search-admin.js";
export type {
  DatabricksAiSearchAdminClient,
  DatabricksAiSearchAdminOperation,
  DatabricksAiSearchAdminPolicy,
  DatabricksSearchIndexSpec,
} from "./ai-search-admin.js";
export { DatabricksServingAdmin, databricksServingAdminTools } from "./serving-admin.js";
export type {
  DatabricksAgentEntity,
  DatabricksAiGatewayUpdate,
  DatabricksCustomModelEntity,
  DatabricksExternalModelEntity,
  DatabricksProvisionedThroughputEntity,
  DatabricksServingEndpointSpec,
} from "./serving-admin.js";
export {
  DatabricksUnityCatalogAdmin,
  databricksUnityCatalogAdminTools,
} from "./unity-catalog-admin.js";
export type { DatabricksPermissionChange, DatabricksSecurableType } from "./unity-catalog-admin.js";
export type { DatabricksNotebookImport } from "./workspace-authoring.js";
export {
  DatabricksSecretsAuthoring,
  databricksSecretsAuthoringTools,
} from "./secrets-authoring.js";
export {
  DatabricksJobs,
  DatabricksRunNotAllowedError,
  DatabricksRunTimeoutError,
  databricksJobs,
  validateDatabricksJobRunPolicy,
  validateDatabricksNotebookRunPolicy,
} from "./jobs.js";
export type {
  DatabricksJobRunPolicy,
  DatabricksJobsClient,
  DatabricksJobsOptions,
  DatabricksJobsWaitOptions,
  DatabricksNotebookRunPolicy,
  DatabricksRunLifeCycleState,
  DatabricksRunOutput,
  DatabricksRunReceipt,
  DatabricksRunResultState,
  DatabricksRunState,
  RunDatabricksJobInput,
  SubmitDatabricksNotebookInput,
} from "./jobs.js";

export { databricksFoundationModelProvider, registerDatabricksModelProvider } from "./model.js";
export { databricksModelBaseUrl, isDatabricksModelService } from "./model.js";
export type {
  DatabricksInferenceMode,
  DatabricksModelOptions,
  DatabricksRequestTags,
} from "./model.js";
export type {
  DatabricksModelService,
  DatabricksModelServiceDiscoveryOptions,
  DatabricksModelProviderService,
} from "./ai-gateway.js";
export { databricksModelProviderSupportsAnthropic } from "./ai-gateway.js";
export {
  createDatabricksAuthenticatedFetch,
  databricksAnthropicGatewayBaseUrl,
} from "./authenticated-fetch.js";
export type { DatabricksAuthenticatedFetchOptions } from "./authenticated-fetch.js";
export { runStatement, parseStatementRows } from "./sql-exec.js";
export type { DatabricksStatementClient, SqlParameter, StatementPoll } from "./sql-exec.js";
export type { DatabricksSqlWarehouse } from "./sql-warehouses.js";
export { chooseDatabricksSqlWarehouse } from "./sql-warehouses.js";
export {
  appServicePrincipalFromEnv,
  databricksHostFromCliProfile,
  databricksIdentity,
  databricksPrincipalFromEnv,
  fabricPrincipalFor,
  onBehalfOfFromHeaders,
} from "./identity.js";
export type {
  DatabricksPrincipal,
  DatabricksPrincipalEnvOptions,
  DatabricksTokenProvider,
  FabricPrincipalOverrides,
} from "./identity.js";
export {
  analyticsCopilotGovernance,
  withGovernance,
  withGovernanceTools,
  databricksApprovalRules,
  databricksGovernancePolicy,
} from "./governance.js";
export type {
  AnalyticsCopilotGovernance,
  AnalyticsCopilotGovernanceOptions,
  DatabricksGovernanceOptions,
  DatabricksLineageRecord,
  DatabricksApprovalRuleOptions,
  DatabricksGovernancePolicyOptions,
  DatabricksGovernanceMetadata,
  DatabricksGovernanceResourceDescriptor,
  DatabricksGovernedResource,
} from "./governance.js";
export {
  extractDatabricksGovernedResources,
  governanceMetadata,
  validateDatabricksGovernanceDescriptor,
} from "./governance.js";
export { lakebaseClient, lakebaseCredentialProvider } from "./lakebase.js";
export { databricksPersistence } from "./persistence.js";
export {
  databricksTelemetry,
  ensureDatabricksTelemetryTables,
  queryDatabricksLineageEvidence,
} from "./telemetry-sink.js";
export { databricksApp } from "./app.js";
export type { DatabricksAppOptions, DatabricksAppRuntime } from "./app.js";
export {
  createDatabricksAppUserAuthenticator,
  DATABRICKS_APP_USER_PERMISSIONS,
  inspectDatabricksAppUserAuthorization,
} from "./app-user-authorization.js";
export type {
  DatabricksAppAuthenticatedPrincipal,
  DatabricksAppUserAuthenticatorOptions,
  DatabricksAppUserAuthorizationInspection,
} from "./app-user-authorization.js";
export type {
  DatabricksLineageEvidenceRow,
  DatabricksTelemetry,
  DatabricksTelemetryOptions,
} from "./telemetry-sink.js";
export type {
  DatabricksPersistence,
  DatabricksPersistenceOptions,
  DatabricksPersistenceStoreModule,
} from "./persistence.js";
export type {
  LakebaseClient,
  LakebaseClientOptions,
  LakebaseCredentialProviderOptions,
  LakebasePoolConfig,
  LakebasePoolFactory,
} from "./lakebase.js";
export { UcVolumesAttachmentStore } from "./volumes-attachment-store.js";
export type { UcVolumesAttachmentStoreOptions } from "./volumes-attachment-store.js";
export { buildMlflowTrace } from "./mlflow-tracing.js";
export type {
  MlflowTraceExporter,
  MlflowTraceInput,
  MlflowTracePayload,
} from "./mlflow-tracing.js";
export { inferenceTableUsageQuery, servingUsageCapture } from "./serving-usage.js";
export type {
  InferenceTableUsageQueryOptions,
  ServingUsageCaptureOptions,
} from "./serving-usage.js";
export { databricksEmbeddings } from "./embeddings.js";
export type { DatabricksEmbeddingsOptions } from "./embeddings.js";
export { databricksAiSearch } from "./vector-search.js";
export type {
  DatabricksAiSearchInputMode,
  DatabricksAiSearchOptions,
  DatabricksAiSearchQueryOptions,
  DatabricksAiSearchQueryResult,
  DatabricksAiSearchRetriever,
  DatabricksAiSearchStrategy,
} from "./vector-search.js";
export { databricksAiQueryTool } from "./ai.js";
export type { DatabricksAiQueryPolicy, DatabricksAiQueryToolOptions } from "./ai.js";
export {
  DatabricksGenieClient,
  DatabricksGenieMessageError,
  DatabricksGenieProtocolError,
  DatabricksGenieResponseLimitError,
  DatabricksGenieTimeoutError,
  databricksGenie,
  databricksGenieTool,
} from "./genie.js";
export type {
  DatabricksGenieAgentReference,
  DatabricksGenieAskInput,
  DatabricksGenieAttachment,
  DatabricksGenieClientOptions,
  DatabricksGenieCommentPage,
  DatabricksGenieConversationPage,
  DatabricksGenieConversationSummary,
  DatabricksGenieMessageComment,
  DatabricksGenieMessagePage,
  DatabricksGenieMessageStatus,
  DatabricksGenieMessageSummary,
  DatabricksGenieQueryAttachment,
  DatabricksGenieQueryResult,
  DatabricksGenieResponse,
  DatabricksGenieSuggestedQuestionsAttachment,
  DatabricksGenieTextAttachment,
  DatabricksGenieToolOptions,
  DatabricksGenieUnknownAttachment,
  DatabricksGenieVisualizationAttachment,
} from "./genie.js";
export {
  databricksLakeflowTools,
  databricksPipelineListTool,
  databricksPipelineStatusTool,
  databricksPipelineStartTool,
  databricksPipelineStopTool,
} from "./lakeflow.js";
export type { DatabricksPipelineRunPolicy, DatabricksPipelinesClient } from "./lakeflow.js";
export { databricksConsumption, databricksConsumptionTool } from "./consumption.js";
export type {
  ConsumptionGroupBy,
  ConsumptionSummary,
  ConsumptionSummaryOptions,
  DatabricksConsumption,
  DatabricksConsumptionOptions,
  DatabricksConsumptionToolOptions,
} from "./consumption.js";
export {
  databricksActualCostSource,
  databricksTenantCostLimit,
  reconcileDatabricksCost,
} from "./cost-budget-actuals.js";
export type {
  DatabricksActualCostSourceOptions,
  DatabricksCostReconciliationOptions,
  DatabricksCostReconciliationResult,
} from "./cost-budget-actuals.js";
export { databricksFeatureLookupTool } from "./feature-serving.js";
export type { DatabricksFeatureLookupToolOptions } from "./feature-serving.js";
export {
  DATABRICKS_AUTHORING_CERTIFICATION_CHECKS,
  DATABRICKS_OPTIONAL_CERTIFICATION_CHECKS,
  DATABRICKS_RELEASE_CERTIFICATION_CHECKS,
  DatabricksCertificationCleanupLedger,
  databricksCertificationTier,
  defaultDatabricksCertificationChecks,
  runDatabricksAuthoringLifecycle,
  runDatabricksCertification,
} from "./certification.js";
export type {
  DatabricksAuthoringLifecycle,
  DatabricksAuthoringLifecycleEvidence,
  DatabricksCertificationCheck,
  DatabricksCertificationBlockingTier,
  DatabricksCertificationEvidence,
  DatabricksCertificationResult,
  DatabricksCleanupResource,
  DatabricksCertificationStatus,
  DatabricksCertificationTier,
  RunDatabricksCertificationOptions,
} from "./certification.js";
export {
  validateDatabricksAppRecoveryEvidence,
  validateDatabricksAppUserIsolationEvidence,
  validateDatabricksDynamicAgentEvidence,
  validateDatabricksCrossTierEvidence,
  validateDatabricksReleaseEvidence,
  validateDatabricksRollingEvidence,
} from "./release-evidence.js";
export type {
  DatabricksAppRecoveryEvidence,
  DatabricksAppUserIsolationEvidence,
  DatabricksDynamicAgentEvidence,
  DatabricksCrossTierEvidenceManifest,
  DatabricksReleaseEvidenceValidation,
  DatabricksRollingEvidenceReport,
  ValidateDatabricksCrossTierEvidenceOptions,
  ValidateDatabricksReleaseEvidenceOptions,
  ValidateDatabricksRollingEvidenceOptions,
} from "./release-evidence.js";
export {
  resolveDatabricksRequiredChecks,
  validateDatabricksCertificationEnvironment,
} from "./certification-environment.js";
export type { DatabricksCertificationEnvironmentIssue } from "./certification-environment.js";
export { createDatabricksAuthoringCertificationChecks } from "./authoring-certification.js";
export type { DatabricksAuthoringCertificationFixtures } from "./authoring-certification.js";
export {
  DATABRICKS_CERTIFICATION_RESOURCE_PREFIX,
  sweepDatabricksAuthoringCertification,
  sweepDatabricksAuthoringCertificationWorkspace,
} from "./certification-sweeper.js";
export type {
  DatabricksCertificationSweepClients,
  DatabricksCertificationSweepOptions,
  DatabricksCertificationSweepResult,
  DatabricksCertificationWorkspaceSweepOptions,
} from "./certification-sweeper.js";
export {
  DATABRICKS_API_VERSIONS,
  DATABRICKS_AUTH_MODES,
  DATABRICKS_PACKAGE_COMPATIBILITY,
  DATABRICKS_SDK_VERSION,
  databricksCompatibilityRecord,
} from "./compatibility.js";
export type { DatabricksCloud, DatabricksWorkspaceCompatibilityRecord } from "./compatibility.js";
export {
  DATABRICKS_CAPABILITIES,
  DATABRICKS_CAPABILITY_EVIDENCE,
  DATABRICKS_PLATFORM_DOMAINS,
  getDatabricksCapability,
  listDatabricksCapabilities,
  missingDatabricksCapabilityEvidence,
} from "./capabilities.js";
export type {
  DatabricksCapability,
  DatabricksApiFidelity,
  DatabricksCapabilityEvidenceReference,
  DatabricksLifecycleOperation,
  DatabricksPlatformDomain,
  DatabricksPlatformDomainId,
  DatabricksCapabilityStatus,
  DatabricksCertificationLevel,
  DatabricksSupportTier,
  DatabricksUpstreamMaturity,
} from "./capabilities.js";
export { databricksSecretsProvider } from "./secrets.js";
export type { DatabricksSecretsProviderOptions } from "./secrets.js";
export { databricksWorkspaceApi } from "./workspace-api.js";
export type {
  DatabricksWorkspaceApi,
  DatabricksWorkspaceApiMethod,
  DatabricksWorkspaceApiOptions,
  DatabricksWorkspaceApiRequestOptions,
} from "./workspace-api.js";

type DatabricksGenieConversationConfig = { agentId: string; enableVisualization?: boolean };

export interface DatabricksGenieManagementConfig {
  /** Durable ownership manifest for untaggable Databricks artifacts such as Genie Agents. */
  resourceStore: import("./managed-resource.js").DatabricksManagedResourceStore;
  /** Server-side parser/policy required before SQL-bearing config becomes model-facing. */
  sqlPolicy?: import("./genie-authoring.js").DatabricksGenieSqlPolicy;
  /** Deletion remains limited to exact Harness-managed resources. */
  delete?: false | "managed-only";
}

export interface DatabricksGenieConfig {
  /** Governed conversations with an existing Genie Agent. */
  conversations?: DatabricksGenieConversationConfig;
  /** Approval-gated lifecycle management. Supplying this object is the explicit write opt-in. */
  manage?: DatabricksGenieManagementConfig;
  /** Explicit Beta Agent Mode streaming configuration. */
  agentMode?: import("./genie-agent-mode.js").DatabricksGenieAgentModeClientOptions & {
    modelTool?: boolean | import("./genie-agent-mode.js").DatabricksGenieAgentModeToolOptions;
  };
}

export { defineDatabricksAgent, resolveToolRefs } from "./agent.js";
export {
  databricksSqlSandbox,
  registerDatabricksSqlSandboxBackend,
  unregisterDatabricksSqlSandboxBackend,
} from "./sql-sandbox.js";
export type {
  DatabricksSqlSandboxOptions,
  DatabricksSqlSandboxRefData,
  DatabricksSqlSandboxRegistrationOptions,
} from "./sql-sandbox.js";
export type { DefineDatabricksAgentOptions } from "./agent.js";
export { MockDatabricksModelProvider } from "./mock-provider.js";
export type { MockDatabricksModelProviderOptions } from "./mock-provider.js";
import { createDatabricksRagChain } from "./rag-chain.js";
export { createDatabricksRagChain };
export type {
  DatabricksRagChain,
  DatabricksRagInput,
  DatabricksRagChainResolvedOptions,
  DatabricksRagRetrievalOptions,
  RagChainPostProcessOptions,
  RagChainPromptOptions,
  RagPreprocess,
  RagStreamEvent,
  RagTurn,
} from "./rag-chain.js";
export {
  exportAgentEvaluationJsonl,
  exportMlflow3EvaluationJsonl,
  ragTurnCitationsScorer,
  ragTurnContainsScorer,
  scoreRagTurn,
  toAgentEvaluationRecord,
  toMlflow3EvaluationRecord,
} from "./rag-eval.js";
export type {
  LegacyAgentEvaluationRecord,
  Mlflow3RagEvaluationRecord,
  RagEvalExpected,
  RagTurnScore,
} from "./rag-eval.js";

/** One checked-in Asset Bundle as configured on `databricks({ assetBundles })`. */
export interface DatabricksAssetBundleEntry {
  /** Directory containing the bundle's `databricks.yml`. */
  bundleDir: string;
  /** Bundle target (dev/prod) passed through to the Databricks CLI. */
  target?: string;
  /** Fingerprint manifest; defaults to an in-memory store. */
  store?: import("./managed-resource.js").DatabricksManagedResourceStore;
}

export interface DatabricksBundleConfig {
  /** Workspace host, e.g. https://adb-1234567890.0.cloud.databricks.com */
  host: string;
  /** Acting identity — UC enforces this principal's grants on every call. */
  principal: DatabricksPrincipal;
  /** Default serving-endpoint name for the returned model provider. */
  model?: string;
  /** AI Gateway or custom Model Serving routing controls. */
  inference?: Omit<DatabricksModelOptions, "host" | "token" | "defaultModel" | "fetchImpl">;
  /** SQL Warehouse id. It does not expose a model tool by itself. */
  warehouseId?: string;
  /** Add a separately enforced SELECT-only `sql_read` tool. Requires `warehouseId`. */
  sqlRead?: boolean;
  /** Add arbitrary/model-selected SQL under an explicit runtime execution policy. */
  sqlExecute?: import("./tools.js").DatabricksSqlExecutionPolicy;
  /** Extra Databricks tools to include; governed alongside the defaults. */
  tools?: ToolDef[];
  governance?: {
    /** When set, sensitive (write/execute) tools route to this approval audience. */
    stewardAudience?: string;
    approvalServices?: string[];
    /** Belt-and-suspenders catalog allowlist enforced before tool execution (UC stays source of truth). */
    catalogs?: string[];
    approvalTtlSeconds?: number;
    /** Label recorded in lineage for the acting principal (never a token). */
    principalLabel?: string;
    /** Sink for audit/lineage records; wire to OTel / onEvent at the app layer. */
    onLineage?: (record: DatabricksLineageRecord) => void;
  };
  /** Explicitly bypass fail-closed model authoring approval. Intended only for local development. */
  allowUnapprovedAuthoring?: boolean;
  jobAuthoring?: boolean;
  /**
   * Governed lifecycle for checked-in Databricks Asset Bundles: adds validate/deploy/run/destroy
   * tools. One entry (`{ bundleDir, target }`) keeps the single-bundle contract and exposes
   * `bundle.assetBundle` with bundle-free tool schemas. A name-keyed map
   * (`{ jobs: { bundleDir }, pipelines: { bundleDir } }`) exposes `bundle.assetBundles` and adds a
   * required `bundle` selector to every tool schema.
   */
  assetBundles?: DatabricksAssetBundleEntry | Record<string, DatabricksAssetBundleEntry>;
  oneOffCompute?: boolean;
  computePolicy?: DatabricksComputePolicy;
  lakeflowAuthoring?: boolean;
  lakeflowEvents?: boolean;
  /**
   * Governed AI Search endpoint/index administration. `allowedOperations` decides which of the six
   * tools are registered, and the index/endpoint allowlists are pinned into their schemas and
   * re-checked at call time. `{ allowAnyAiSearchAdmin: true }` is the explicit unbounded opt-out.
   */
  aiSearchAdmin?: DatabricksAiSearchAdminPolicy | false;
  servingAdmin?: boolean;
  ucAdmin?: boolean;
  ucDestructiveAdmin?: boolean;
  workspaceWrite?: boolean;
  secretsWrite?: boolean;
  /** Server-side resolver required by the secret-reference write tool. */
  secretProvider?: SecretProvider;
  /** Lakebase (managed Postgres) connection for durable session/journal state. */
  lakebase?: {
    host: string;
    database: string;
    user: string;
    /** Lakebase endpoint resource name used for database credential exchange. */
    endpoint: string;
    port?: number;
    ssl?: boolean | { rejectUnauthorized?: boolean };
    poolFactory?: LakebasePoolFactory;
  };
  /**
   * Builds a SessionStore from the Lakebase client. Injected so this package stays decoupled from
   * `@fabric-harness/node` — e.g. `(client) => new PostgresSessionStore({ client })`.
   */
  sessionStoreFactory?: (client: LakebaseClient) => SessionStore;
  /** Databricks AI Search for RAG. Adds a governed `search` tool and exposes `retriever`. */
  aiSearch?: {
    /** Full index name `catalog.schema.index`. */
    index: string;
    /** Column holding the chunk text. */
    textColumn: string;
    columns?: string[];
    idColumn?: string;
    /** Query representation. Defaults to `text`. */
    inputMode?: import("./vector-search.js").DatabricksAiSearchInputMode;
    /** Native Databricks retrieval algorithm. Text input defaults to `hybrid`. */
    strategy?: import("./vector-search.js").DatabricksAiSearchStrategy;
    /** Serving-endpoint name for query embeddings used by vector input. */
    embeddingEndpoint?: string;
    scoreThreshold?: number;
    queryColumns?: string[];
    sortColumns?: string[];
    facets?: string[];
    reranker?: import("@databricks/sdk-vectorsearch/v1").RerankerConfig;
    columnsToRerank?: string[];
    /** Override the search tool name (default `search`). */
    searchToolName?: string;
  };
  /** Add bounded `ai_query` inference. Requires `warehouseId` and an endpoint policy. */
  aiFunctions?: DatabricksAiQueryPolicy;
  /** Add the governed Genie tool for natural-language analytics over an existing Genie Agent. */
  genie?: DatabricksGenieConfig;
  /** Expose the typed Unity Catalog Agent Services Beta lifecycle client. */
  agentServices?: DatabricksAgentServicesOptions;
  /** Expose typed Supervisor Agent discovery, lifecycle, and Responses invocation. */
  supervisorAgents?: DatabricksSupervisorAgentsOptions;
  /** Expose Databricks-managed long-term memory without merging it into Harness session state. */
  managedMemory?: DatabricksManagedMemoryOptions;
  /**
   * Add Lakeflow pipeline tools. `true` is read-only list/status; supplying `runPolicy` explicitly
   * adds bounded start/stop tools.
   */
  lakeflow?: boolean | { runPolicy: DatabricksPipelineRunPolicy };
  /** Add the System-Tables consumption tool + expose `bundle.consumption`. Requires `warehouseId`. */
  consumption?: boolean;
  /** Add a governed low-latency feature-lookup tool against a Feature Serving endpoint. */
  featureServing?: { endpoint: string };
  /** Databricks managed MCP or registered MCP Services. Requires `databricksWithManagedMcp()`. */
  mcp?: DatabricksManagedMcpServerConfig[];
  fetchImpl?: typeof fetch;
}

export interface DatabricksBundle {
  /** Rotating token provider shared by model inference and native Databricks SDK clients. */
  identity: DatabricksTokenProvider;
  /** Official generated Databricks service clients bound to the bundle principal. */
  sdk: DatabricksSdkClients;
  modelProvider: ModelProvider;
  /** Governed Databricks tools (lineage-stamped, catalog-allowlisted). */
  tools: ToolDef[];
  /** Approval routing + egress allowlist for the tool set. Merge into the agent's policy. */
  policy: CapabilityPolicy;
  lakebase?: LakebaseClient;
  store?: SessionStore;
  /** Mount Databricks workspace files as a read-only context source. */
  workspaceSource: (
    rootPath: string,
    options?: DatabricksWorkspaceSourceOptions,
  ) => FilesystemSource;
  /** AI Search retriever, when `aiSearch` is configured. */
  retriever?: DatabricksAiSearchRetriever;
  /** Query-embedding provider, when `aiSearch.embeddingEndpoint` is configured. */
  embeddings?: EmbeddingProvider;
  /** System-Tables consumption reporter, when `consumption` is enabled (needs `warehouseId`). */
  consumption?: DatabricksConsumption;
  /** Typed Genie management client when `genie.manage` is configured. */
  genieAdmin?: DatabricksGenieAdmin;
  /** Typed Beta Agent Mode streaming client when `genie.agentMode` is configured. */
  genieAgentMode?: DatabricksGenieAgentModeClient;
  /** Typed Unity Catalog Agent Services client when `agentServices` is configured. */
  agentServices?: DatabricksAgentServices;
  /** Existing Databricks agent endpoints as governed tools/subagents. */
  agentEndpoints: {
    client: DatabricksAgentEndpointClient;
    tool(options: DatabricksManagedAgentToolOptions): ToolDef;
  };
  /** Typed Beta Supervisor Agent composition when explicitly acknowledged. */
  supervisorAgents?: DatabricksSupervisorAgents;
  /** Explicit Databricks-managed long-term memory; never silently used as Harness session state. */
  managedMemory?: DatabricksManagedMemoryClient;
  /**
   * Governed Asset Bundle lifecycle when exactly one bundle is configured — the single-bundle
   * `assetBundles` shape, or a named map holding one entry.
   */
  assetBundle?: DatabricksAssetBundleLifecycle;
  /** Governed Asset Bundle lifecycles by name, when `assetBundles` uses the named-map shape. */
  assetBundles?: Readonly<Record<string, DatabricksAssetBundleLifecycle>>;
  /** SDK-gap discovery methods kept behind the bundle rather than exposing raw transport. */
  aiGateway: {
    ensure(): Promise<void>;
    listModelServices(
      options?: DatabricksModelServiceDiscoveryOptions,
    ): Promise<DatabricksModelService[]>;
    listModelProviderServices(): Promise<
      import("./ai-gateway.js").DatabricksModelProviderService[]
    >;
  };
  sqlWarehouses: {
    list(): Promise<import("./sql-warehouses.js").DatabricksSqlWarehouse[]>;
  };
  /** Build an MLflow trace exporter without exposing the raw protocol transport. */
  mlflowTraceExporter(options: {
    experimentId: string;
    onError?: (context: string, error: unknown) => void;
  }): MlflowTraceExporter;
  /** Create an immutable request-scoped bundle from an already verified principal. */
  forPrincipal(input: {
    tokenProvider: DatabricksTokenProvider;
    principal: FabricPrincipal;
  }): DatabricksBundle;
}

export interface DatabricksManagedMcpBundle extends DatabricksBundle {
  managedMcp: {
    connections: Awaited<ReturnType<typeof connectDatabricksManagedMcpServer>>[];
    reconnect(): Promise<void>;
    close(): Promise<void>;
  };
  /** Rebuild every remote connection under a verified request-scoped principal. */
  forPrincipalWithManagedMcp(input: {
    tokenProvider: DatabricksTokenProvider;
    principal: FabricPrincipal;
  }): Promise<DatabricksManagedMcpBundle>;
}

/**
 * One-call wiring for a Databricks-native agent: model serving, a UC principal threaded through
 * model + data + state, governed tools, an approval/egress policy, and (optionally) Lakebase-backed
 * durable state. Also registers `databricks/*` model refs. Everything is layered on
 * Unity Catalog — never a replacement for it.
 */
export function databricks(config: DatabricksBundleConfig): DatabricksBundle {
  if (config.mcp?.length) {
    throw new Error(
      "databricks(): managed MCP discovery is asynchronous; use await databricksWithManagedMcp(config).",
    );
  }
  return buildDatabricksBundle(config);
}

/** Build a Databricks bundle after discovering and classifying managed MCP tools. */
export async function databricksWithManagedMcp(
  config: DatabricksBundleConfig,
  scope?: { tokenProvider: DatabricksTokenProvider; principal: FabricPrincipal },
): Promise<DatabricksManagedMcpBundle> {
  const identity = scope?.tokenProvider ?? databricksIdentity(config.principal);
  const principal = scope?.principal ?? fabricPrincipalFor(config.principal);
  const connections: Awaited<ReturnType<typeof connectDatabricksManagedMcpServer>>[] = [];
  try {
    for (const server of config.mcp ?? []) {
      connections.push(
        await connectDatabricksManagedMcpServer({
          host: config.host,
          tokenProvider: identity,
          server,
          ...(config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}),
        }),
      );
    }
    const bundle = buildDatabricksBundle(config, {
      identity,
      principal,
      managedMcpTools: connections.flatMap((connection) => connection.tools),
    });
    const managedMcp = {
      connections,
      reconnect: async () => {
        await Promise.all(connections.map((connection) => connection.reconnect()));
      },
      close: async () => {
        await Promise.all(connections.map((connection) => connection.close()));
      },
    };
    bundle.forPrincipal = () => {
      throw new Error(
        "Managed MCP connections are principal-bound; use await bundle.forPrincipalWithManagedMcp(...).",
      );
    };
    return Object.assign(bundle, {
      managedMcp,
      forPrincipalWithManagedMcp: (input: {
        tokenProvider: DatabricksTokenProvider;
        principal: FabricPrincipal;
      }) => databricksWithManagedMcp(config, input),
    });
  } catch (error) {
    await Promise.all(connections.map((connection) => connection.close().catch(() => undefined)));
    throw error;
  }
}

function buildDatabricksBundle(
  config: DatabricksBundleConfig,
  scope?: {
    identity: DatabricksTokenProvider;
    principal: FabricPrincipal;
    appStore?: SessionStore;
    managedMcpTools?: ToolDef[];
  },
): DatabricksBundle {
  registerDatabricksModelProvider();
  // Before validateAuthoringConfig so a malformed `assetBundles` map reports its own error instead of
  // the misleading "authoring services are not approval-gated".
  const assetBundles = buildAssetBundleLifecycles(config);
  const genie = normalizeGenieConfig(config);
  validateAuthoringConfig(config, genie);
  const identity = scope?.identity ?? databricksIdentity(config.principal);
  const executingPrincipal = scope?.principal ?? fabricPrincipalFor(config.principal);
  const native = createDatabricksNativeClients({
    host: config.host,
    credentials: scope
      ? databricksCredentialsFromTokenProvider(identity, executingPrincipal.id)
      : databricksCredentials(config.principal),
    tokenProvider: identity,
    ...(config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}),
  });
  const client = native.rawProtocol;
  const agentEndpointClient = new DatabricksAgentEndpointClient(client);
  const genieAgentModeClient = genie.agentMode
    ? databricksGenieAgentMode(client, genie.agentMode)
    : undefined;
  const agentServicesClient = config.agentServices
    ? databricksAgentServices(client, config.agentServices)
    : undefined;
  const supervisorAgentsClient = config.supervisorAgents
    ? databricksSupervisorAgents(
        native.supervisorAgents,
        agentEndpointClient,
        config.supervisorAgents,
      )
    : undefined;
  const managedMemoryClient = config.managedMemory
    ? databricksManagedMemory(client, config.managedMemory)
    : undefined;
  const namedAssetBundles =
    assetBundles instanceof Map ? Object.freeze(Object.fromEntries(assetBundles)) : undefined;
  // Convenience alias for the common one-bundle case, whichever config shape produced it.
  const assetBundle =
    assetBundles instanceof Map
      ? assetBundles.size === 1
        ? [...assetBundles.values()][0]
        : undefined
      : assetBundles;

  let embeddings: EmbeddingProvider | undefined;
  let retriever: DatabricksAiSearchRetriever | undefined;
  let searchTool: ToolDef | undefined;
  const aiSearch = config.aiSearch;
  if (aiSearch) {
    if (aiSearch.embeddingEndpoint)
      embeddings = databricksEmbeddings({
        client: native.modelServingQuery,
        endpoint: aiSearch.embeddingEndpoint,
      });
    retriever = databricksAiSearch({
      client: native.vectorSearch,
      rawProtocolClient: client,
      index: aiSearch.index,
      textColumn: aiSearch.textColumn,
      ...(aiSearch.columns ? { columns: aiSearch.columns } : {}),
      ...(aiSearch.idColumn ? { idColumn: aiSearch.idColumn } : {}),
      ...(aiSearch.inputMode ? { inputMode: aiSearch.inputMode } : {}),
      ...(aiSearch.strategy ? { strategy: aiSearch.strategy } : {}),
      ...(embeddings ? { embeddings } : {}),
      ...(aiSearch.scoreThreshold !== undefined ? { scoreThreshold: aiSearch.scoreThreshold } : {}),
      ...(aiSearch.queryColumns ? { queryColumns: aiSearch.queryColumns } : {}),
      ...(aiSearch.sortColumns ? { sortColumns: aiSearch.sortColumns } : {}),
      ...(aiSearch.facets ? { facets: aiSearch.facets } : {}),
      ...(aiSearch.reranker ? { reranker: aiSearch.reranker } : {}),
      ...(aiSearch.columnsToRerank ? { columnsToRerank: aiSearch.columnsToRerank } : {}),
    });
    searchTool = createSearchTool(retriever, {
      ...(aiSearch.searchToolName ? { name: aiSearch.searchToolName } : {}),
      metadata: { provider: "databricks", service: "ai-search" },
    });
  }

  const rawTools: ToolDef[] = [
    ...(config.sqlRead && config.warehouseId
      ? [databricksSqlReadTool(native.statements, { warehouseId: config.warehouseId }) as ToolDef]
      : []),
    ...(config.sqlExecute && config.warehouseId
      ? [
          databricksSqlTool(native.statements, config.sqlExecute, {
            warehouseId: config.warehouseId,
          }) as ToolDef,
        ]
      : []),
    unityCatalogTablesTool(native.tables) as ToolDef,
    databricksTableInfoTool(native.tables) as ToolDef,
    ...(searchTool ? [searchTool] : []),
    ...(config.aiFunctions && config.warehouseId
      ? [
          databricksAiQueryTool(native.statements, config.aiFunctions, {
            warehouseId: config.warehouseId,
          }) as ToolDef,
        ]
      : []),
    ...(genie.conversations
      ? [
          databricksGenieTool(native.genie, {
            ...genie.conversations,
            rawProtocolClient: client,
          }) as ToolDef,
        ]
      : []),
    ...(genieAgentModeClient && genie.agentMode?.modelTool
      ? [
          databricksGenieAgentModeTool(
            genieAgentModeClient,
            genie.agentMode.agentId,
            genie.agentMode.modelTool === true ? {} : genie.agentMode.modelTool,
          ),
        ]
      : []),
    ...(genie.manage
      ? databricksGenieAuthoringTools(
          {
            genie: native.genie,
            permissions: native.accessManagement,
          },
          {
            managedResourceStore: genie.manage.resourceStore,
            principal: executingPrincipal.ucPrincipal ?? executingPrincipal.id,
            destructive: genie.manage.delete === "managed-only",
            ...(genie.manage.sqlPolicy ? { sqlPolicy: genie.manage.sqlPolicy } : {}),
          },
        )
      : []),
    ...(config.lakeflow
      ? databricksLakeflowTools(
          native.pipelines,
          typeof config.lakeflow === "object" ? config.lakeflow.runPolicy : undefined,
        )
      : []),
    ...(config.consumption && config.warehouseId
      ? [
          databricksConsumptionTool(native.statements, {
            warehouseId: config.warehouseId,
          }) as ToolDef,
        ]
      : []),
    ...(config.featureServing
      ? [
          databricksFeatureLookupTool(native.modelServingQuery, {
            endpoint: config.featureServing.endpoint,
          }) as ToolDef,
        ]
      : []),
    ...(config.jobAuthoring || config.oneOffCompute
      ? databricksJobsAuthoringTools(native.jobs, config.computePolicy ?? {}, {
          ...(config.oneOffCompute !== undefined ? { oneOffCompute: config.oneOffCompute } : {}),
        })
      : []),
    ...(config.lakeflowAuthoring
      ? databricksLakeflowAuthoringTools(native.pipelines, {
          ...(config.lakeflowEvents && config.warehouseId
            ? { warehouseId: config.warehouseId, statements: native.statements }
            : {}),
        })
      : []),
    ...(assetBundles ? databricksAssetBundleTools(assetBundles) : []),
    ...(config.aiSearchAdmin
      ? databricksAiSearchAdminTools(native.vectorSearch, config.aiSearchAdmin)
      : []),
    ...(config.servingAdmin ? databricksServingAdminTools(native.modelServing) : []),
    ...(config.ucAdmin
      ? databricksUnityCatalogAdminTools(
          {
            catalogs: native.catalogs,
            schemas: native.schemas,
            volumes: native.volumes,
            grants: native.grants,
          },
          {
            ...(config.ucDestructiveAdmin !== undefined
              ? { destructive: config.ucDestructiveAdmin }
              : {}),
          },
        )
      : []),
    ...(config.workspaceWrite ? databricksWorkspaceAuthoringTools(client) : []),
    ...(config.secretsWrite && config.secretProvider
      ? databricksSecretsAuthoringTools(native.secrets, config.secretProvider)
      : []),
    ...(scope?.managedMcpTools ?? []),
    ...(config.tools ?? []),
  ];
  const authoringServices = enabledAuthoringServices(config);
  const approvalServices = config.governance?.approvalServices;
  const approvalRequiredToolNames = new Set(
    rawTools
      .filter((tool) => {
        const effect = (tool.metadata as Record<string, unknown> | undefined)?.effect;
        if (effect !== "write" && effect !== "execute") return false;
        if (!approvalServices) return true;
        const service = (tool.metadata as Record<string, unknown> | undefined)?.service;
        return typeof service === "string" && approvalServices.includes(service);
      })
      .map((tool) => tool.name),
  );
  for (const tool of rawTools) {
    const effect = (tool.metadata as Record<string, unknown> | undefined)?.effect;
    if (effect === "write" || effect === "execute")
      validateDatabricksGovernanceDescriptor(tool, true);
  }
  const tools = withGovernanceTools(rawTools, {
    principal:
      scope?.principal.ucPrincipal ??
      scope?.principal.id ??
      config.governance?.principalLabel ??
      principalLabel(config.principal),
    ...(config.governance?.onLineage ? { onLineage: config.governance.onLineage } : {}),
    ...(config.governance?.catalogs ? { catalogs: config.governance.catalogs } : {}),
    executingPrincipal,
    ...(config.governance?.stewardAudience && !config.allowUnapprovedAuthoring
      ? { requireApprovalGrantForTools: [...approvalRequiredToolNames] }
      : {}),
  });

  const policy = databricksGovernancePolicy({
    tools,
    host: config.host,
    ...(config.governance?.stewardAudience
      ? { stewardAudience: config.governance.stewardAudience }
      : {}),
    ...(config.governance?.approvalServices
      ? { approvalServices: config.governance.approvalServices }
      : {}),
    ...(config.governance?.approvalTtlSeconds !== undefined
      ? { ttlSeconds: config.governance.approvalTtlSeconds }
      : {}),
  });

  const bundle: DatabricksBundle = {
    identity,
    sdk: stableDatabricksSdkClients(native),
    modelProvider: databricksFoundationModelProvider({
      host: config.host,
      token: identity,
      ...(config.model ? { defaultModel: config.model } : {}),
      ...(config.inference ?? {}),
      ...(config.fetchImpl ? { fetchImpl: config.fetchImpl } : {}),
    }),
    tools,
    policy,
    workspaceSource: (rootPath, options) =>
      databricksWorkspaceSource(client, rootPath, options ?? {}),
    ...(retriever ? { retriever } : {}),
    ...(embeddings ? { embeddings } : {}),
    ...(config.consumption && config.warehouseId
      ? {
          consumption: databricksConsumption(native.statements, {
            warehouseId: config.warehouseId,
          }),
        }
      : {}),
    ...(genie.manage
      ? {
          genieAdmin: databricksGenieAdmin(
            {
              genie: native.genie,
              permissions: native.accessManagement,
            },
            {
              managedResourceStore: genie.manage.resourceStore,
            },
          ),
        }
      : {}),
    ...(genieAgentModeClient ? { genieAgentMode: genieAgentModeClient } : {}),
    ...(agentServicesClient ? { agentServices: agentServicesClient } : {}),
    agentEndpoints: {
      client: agentEndpointClient,
      tool: (options) => databricksManagedAgentTool(agentEndpointClient, options),
    },
    ...(supervisorAgentsClient ? { supervisorAgents: supervisorAgentsClient } : {}),
    ...(managedMemoryClient ? { managedMemory: managedMemoryClient } : {}),
    ...(assetBundle ? { assetBundle } : {}),
    ...(namedAssetBundles ? { assetBundles: namedAssetBundles } : {}),
    aiGateway: {
      ensure: () => ensureDatabricksAiGateway(client),
      listModelServices: (options) => listDatabricksModelServices(client, options),
      listModelProviderServices: () => listDatabricksModelProviderServices(client),
    },
    sqlWarehouses: { list: () => listDatabricksSqlWarehouses(client) },
    mlflowTraceExporter: (options) => mlflowTraceExporter({ client, ...options }),
    forPrincipal(input) {
      const scoped = buildDatabricksBundle(config, {
        identity: input.tokenProvider,
        principal: input.principal,
        ...(bundle.store ? { appStore: bundle.store } : {}),
      });
      if (bundle.store) scoped.store = bundle.store;
      return scoped;
    },
  };

  if (config.lakebase) {
    const lakebase = lakebaseClient({
      ...config.lakebase,
      credentialClient: native.postgres,
    });
    bundle.lakebase = lakebase;
    if (scope?.appStore) bundle.store = scope.appStore;
    else if (config.sessionStoreFactory) bundle.store = config.sessionStoreFactory(lakebase);
  }
  return bundle;
}

/**
 * Build the configured bundle lifecycles: one instance for the single-bundle shape, a name-keyed Map
 * for the named shape. Instances are shared with the tools so a default in-memory managed-resource
 * store stays consistent between `bundle.assetBundle(s)` and the model-facing tools.
 */
function buildAssetBundleLifecycles(
  config: DatabricksBundleConfig,
): DatabricksAssetBundleLifecycle | Map<string, DatabricksAssetBundleLifecycle> | undefined {
  const assetBundles = config.assetBundles;
  if (assetBundles === undefined) return undefined;
  const label = config.governance?.principalLabel ?? principalLabel(config.principal);
  // Only the documented fields reach either shape's lifecycle: config is data, so a structurally
  // compatible extra like `principalLabel` or `run` must never override the governance label below.
  if (isDatabricksAssetBundleEntry(assetBundles))
    return databricksAssetBundleLifecycle({
      bundleDir: assetBundles.bundleDir,
      ...(assetBundles.target !== undefined ? { target: assetBundles.target } : {}),
      ...(assetBundles.store !== undefined ? { store: assetBundles.store } : {}),
      principalLabel: label,
    });
  // Shape is validated before the entries are re-materialized below, so a stray array cannot reach
  // enumeration and become a bundle named "0".
  requireDatabricksAssetBundleMap(assetBundles);
  return databricksAssetBundleLifecycles(
    // Cast at the boundary into the validating helper: these are unvalidated config candidates, and
    // each entry's `bundleDir` is enforced there rather than trusted here.
    Object.fromEntries(
      Object.entries(assetBundles).map(([name, entry]) => [name, assetBundleEntryOptions(entry)]),
    ) as DatabricksAssetBundleMapOptions,
    { principalLabel: label },
  );
}

/** Pick the documented fields off one unvalidated config entry; `bundleDir` is checked downstream. */
function assetBundleEntryOptions(entry: unknown): Partial<DatabricksAssetBundleEntry> {
  if (entry === null || typeof entry !== "object") return {};
  const { bundleDir, target, store } = entry as Partial<DatabricksAssetBundleEntry>;
  return {
    ...(bundleDir !== undefined ? { bundleDir } : {}),
    ...(target !== undefined ? { target } : {}),
    ...(store !== undefined ? { store } : {}),
  };
}

function enabledAuthoringServices(
  config: DatabricksBundleConfig,
  genie: NormalizedDatabricksGenieConfig = normalizeGenieConfig(config),
): string[] {
  return [
    ...new Set([
      ...(config.jobAuthoring || config.oneOffCompute ? ["jobs"] : []),
      ...(genie.manage ? ["genie"] : []),
      ...(config.lakeflowAuthoring ? ["lakeflow"] : []),
      ...(config.assetBundles ? ["bundles"] : []),
      ...(config.aiSearchAdmin ? ["ai-search"] : []),
      ...(config.servingAdmin ? ["serving"] : []),
      ...(config.ucAdmin ? ["unity-catalog"] : []),
      ...(config.workspaceWrite ? ["workspace"] : []),
      ...(config.secretsWrite ? ["secrets"] : []),
    ]),
  ];
}

function validateAuthoringConfig(
  config: DatabricksBundleConfig,
  genie: NormalizedDatabricksGenieConfig = normalizeGenieConfig(config),
): void {
  const services = enabledAuthoringServices(config, genie);
  if (config.ucDestructiveAdmin && !config.ucAdmin)
    throw new Error("databricks: ucDestructiveAdmin requires ucAdmin: true.");
  if ((config.aiSearchAdmin as unknown) === true)
    throw new Error(
      "databricks: aiSearchAdmin no longer accepts a boolean. Bound it with " +
        "{ allowedOperations: [...], allowedIndexes: [...] }, or opt out explicitly with " +
        "{ allowAnyAiSearchAdmin: true }.",
    );
  if (services.length === 0) return;
  if (config.lakeflowEvents && !config.lakeflowAuthoring)
    throw new Error("databricks: lakeflowEvents requires lakeflowAuthoring: true.");
  if (config.lakeflowEvents && !config.warehouseId)
    throw new Error("databricks: lakeflowEvents requires warehouseId for event_log queries.");
  if (config.secretsWrite && !config.secretProvider)
    throw new Error("databricks: secretsWrite requires a server-side secretProvider.");
  if (config.jobAuthoring || config.oneOffCompute)
    validateDatabricksComputePolicy(config.computePolicy ?? {});
  const audience = config.governance?.stewardAudience;
  if (!config.allowUnapprovedAuthoring && !audience) {
    throw new Error(
      `databricks: authoring services are not approval-gated: ${services.join(", ")}. Configure governance.stewardAudience, or explicitly set allowUnapprovedAuthoring: true.`,
    );
  }
}

interface NormalizedDatabricksGenieConfig {
  conversations?: DatabricksGenieConversationConfig;
  manage?: DatabricksGenieManagementConfig;
  agentMode?: NonNullable<DatabricksGenieConfig["agentMode"]>;
}

function normalizeGenieConfig(config: DatabricksBundleConfig): NormalizedDatabricksGenieConfig {
  const conversations = config.genie?.conversations;
  const manage = config.genie?.manage;
  const agentMode = config.genie?.agentMode;
  return {
    ...(conversations ? { conversations } : {}),
    ...(manage ? { manage } : {}),
    ...(agentMode ? { agentMode } : {}),
  };
}

function principalLabel(principal: DatabricksPrincipal): string {
  if (principal.kind === "service-principal") return `sp:${principal.clientId}`;
  if (principal.kind === "cli-profile")
    return principal.profile ? `cli:${principal.profile}` : "cli-profile";
  // pat / on-behalf-of: prefer an explicit label (e.g. the channel actor / user id) over the bare kind.
  return principal.label ?? principal.kind;
}

export interface DatabricksRagChainOptions {
  /** Existing bundle from `databricks({ aiSearch: ... })`. */
  bundle?: DatabricksBundle;
  /** Or pass the same config shape as `databricks()` (must include `aiSearch`). */
  databricks?: DatabricksBundleConfig;
  modelProvider?: import("@fabric-harness/sdk").ModelProvider;
  retriever?: import("@fabric-harness/sdk").Retriever;
  model?: string;
  topK?: number;
  filter?: import("@fabric-harness/sdk").RetrieveOptions["filter"];
  /** Native AI Search defaults for every turn. */
  retrieval?: import("./rag-chain.js").DatabricksRagRetrievalOptions;
  preprocess?: import("./rag-chain.js").RagPreprocess;
  prompt?: import("./rag-chain.js").RagChainPromptOptions;
  postProcess?: import("./rag-chain.js").RagChainPostProcessOptions;
}

/**
 * Cookbook-style online RAG chain over **native** Databricks AI Search + Model Serving.
 *
 * ```ts
 * const chain = databricksRagChain({
 *   databricks: { host, principal, model, aiSearch: { index, textColumn, idColumn } },
 * });
 * const turn = await chain.invoke({ question: 'How do I reset my password?' });
 * // Export for Mosaic Agent Evaluation:
 * // writeFileSync('eval.jsonl', exportAgentEvaluationJsonl([toAgentEvaluationRecord(turn)]));
 * ```
 */
export function databricksRagChain(
  options: DatabricksRagChainOptions,
): import("./rag-chain.js").DatabricksRagChain {
  const aiSearch = options.databricks?.aiSearch;
  const bundle =
    options.bundle ?? (options.databricks ? databricks(options.databricks) : undefined);
  if (!bundle && !(options.retriever && options.modelProvider)) {
    throw new Error(
      "databricksRagChain requires `bundle`, `databricks` config with aiSearch, or both retriever and modelProvider.",
    );
  }
  const modelProvider = options.modelProvider ?? bundle?.modelProvider;
  if (!modelProvider) {
    throw new Error(
      "databricksRagChain requires a model provider from `bundle`, `databricks`, or `modelProvider`.",
    );
  }
  const retriever = options.retriever ?? bundle?.retriever;
  if (!retriever) {
    throw new Error(
      "databricksRagChain requires AI Search: set databricks.aiSearch or pass retriever.",
    );
  }
  return createDatabricksRagChain({
    modelProvider,
    retriever,
    ...(bundle ? { policy: bundle.policy, tools: bundle.tools } : {}),
    ...(options.model ? { model: options.model } : {}),
    ...(options.topK !== undefined ? { topK: options.topK } : {}),
    ...(options.filter ? { filter: options.filter } : {}),
    ...(options.retrieval ? { retrieval: options.retrieval } : {}),
    ...(options.preprocess ? { preprocess: options.preprocess } : {}),
    ...(options.prompt ? { prompt: options.prompt } : {}),
    ...(options.postProcess ? { postProcess: options.postProcess } : {}),
    ...(aiSearch?.index ? { index: aiSearch.index } : {}),
  });
}
